import type { SxProps, Theme } from '@mui/material';
import type { CSSObject } from '@mui/system';
import { makeStyles } from 'tss-react/mui';
import { BODY1_LINE_HEIGHT } from 'theme';
import { LAYERS_DRAWER_WIDTH } from 'features/mapping/mapping.constants';
import {
  DOWNLOAD_APP_BANNER_OFFSET_CSS_VALUE,
  TOOLBAR_HEIGHT_DESKTOP_PX,
} from '../../../../../constants';

import {
  BASEMAP_PREVIEW_HEIGHT_PX,
  BASEMAP_SELECTION_BORDER_PX,
  type BasemapPreviewLayout,
} from './layersDrawer.shared';

export const getActiveLayersChipColors = (isActive: boolean): CSSObject => ({
  bgcolor: isActive ? 'alert.normal.main' : 'alert.neutral.main',
  color: isActive ? 'alert.normal.contrastText' : 'alert.neutral.contrastText',
});

/** Expanded layer group / section list (12px vertical padding, secondary surface). */
export const layersDrawerListPaddingSx: SxProps<Theme> = {
  pt: 1,
  pb: 2,
  bgcolor: 'background.secondary',
};

/** Layer row layout: 16px horizontal padding, content + trailing control in two columns. */
export const layersDrawerRowSx: SxProps<Theme> = {
  py: 1,
  px: 2,
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) auto',
  columnGap: 1.5,
  alignItems: 'center',
  width: '100%',
  boxSizing: 'border-box',
};

export const layersDrawerRowContentSx: SxProps<Theme> = {
  minWidth: 0,
};

/** Trailing chip + chevron (+ switch) cluster, vertically centered on the row via grid. */
export const layersDrawerRowActionsSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
  gap: 0.5,
};

/** Switch in layer rows: full 44×26 track, no ListItem `edge="end"` inset. */
export const layersDrawerSwitchSx: SxProps<Theme> = {
  flexShrink: 0,
  m: 0,
  alignSelf: 'center',
};

/** Row action + switch in the trailing grid column. */
export const layersDrawerRowActionSlotSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
  gap: 1.5,
};

export const layersDrawerRowTitleSx: SxProps<Theme> = {
  typography: 'body1',
  fontWeight: 600,
  lineHeight: BODY1_LINE_HEIGHT,
  whiteSpace: 'normal',
  overflowWrap: 'break-word',
};

export const layersDrawerChevronButtonSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 'auto',
  minWidth: 0,
  height: 'auto',
  flexShrink: 0,
  p: 0,
  lineHeight: 0,
};

/** Nested controls under an expanded layer row (e.g. weather sensor selector). */
export const layersDrawerControlItemChildrenSx: SxProps<Theme> = {
  width: '100%',
  px: 2,
  pt: 1.5,
  pb: 1,
};

export const useLayersDrawerStyles = makeStyles()((theme) => ({
  drawer: {
    width: LAYERS_DRAWER_WIDTH,
  },
  drawerPaper: {
    paddingTop: `calc(${TOOLBAR_HEIGHT_DESKTOP_PX}px + env(safe-area-inset-top) + ${DOWNLOAD_APP_BANNER_OFFSET_CSS_VALUE})`,
    paddingBottom: `env(safe-area-inset-bottom)`,
    width: LAYERS_DRAWER_WIDTH,
    zIndex: theme.zIndex.drawer + 2,
    backgroundColor: theme.palette.background.default,
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
  },
}));

export const useLayerGroupsPanelStyles = makeStyles()((theme) => ({
  closeButton: {
    position: 'absolute',
    top: 4,
    right: 4,
    zIndex: 1,
  },
  contentContainer: {
    padding: '8px 0px',
    paddingTop: 16,
    [theme.breakpoints.down('phone')]: {
      paddingTop: 0,
    },
  },
  mapTypeDivider: {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

const getBasemapPreviewWidth = (
  previewLayout: BasemapPreviewLayout,
): number | string =>
  previewLayout === 'square' ? BASEMAP_PREVIEW_HEIGHT_PX : '100%';

export const useBasemapStyleSelectionStyles = makeStyles<{
  previewLayout: BasemapPreviewLayout;
}>()((theme, { previewLayout }) => {
  const innerSize = BASEMAP_PREVIEW_HEIGHT_PX - 2 * BASEMAP_SELECTION_BORDER_PX;
  const selectedBorderColor = theme.palette.brand[500];

  return {
    container: {
      height: BASEMAP_PREVIEW_HEIGHT_PX,
      width: getBasemapPreviewWidth(previewLayout),
      borderRadius: Number(theme.shape.borderRadius) * 2,
      overflow: 'hidden',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      border: `${BASEMAP_SELECTION_BORDER_PX}px solid transparent`,
      boxSizing: 'border-box',
      flexShrink: previewLayout === 'fluid' ? 1 : 0,
      minWidth: 0,
      marginTop: BASEMAP_SELECTION_BORDER_PX,
      ...(previewLayout === 'fluid' && {
        alignSelf: 'stretch',
      }),
    },
    activeBackground: {
      borderColor: selectedBorderColor,
      backgroundColor: 'transparent',
    },
    media: {
      display: 'block',
      objectFit: 'cover',
      ...(previewLayout === 'fluid'
        ? {
            width: '100%',
            height: '100%',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }
        : {
            height: innerSize,
            width: innerSize,
          }),
    },
  };
});

export const useMapBaseLayerSelectorStyles = makeStyles<{
  previewLayout: BasemapPreviewLayout;
}>()((theme, { previewLayout }) => ({
  radioGroup: {
    display: 'flex',
    flexDirection: 'row',
    paddingLeft:
      previewLayout === 'fluid' ? theme.spacing(1) : theme.spacing(2),
    paddingRight:
      previewLayout === 'fluid' ? theme.spacing(1) : theme.spacing(2),
    marginTop: previewLayout === 'fluid' ? -4 : 0,
    paddingBottom: 8,
    gap: previewLayout === 'fluid' ? 12 : theme.spacing(1),
    justifyContent: 'stretch',
    width: '100%',
    boxSizing: 'border-box',
  },
  option: {
    position: 'relative',
    flexDirection: 'column',
    alignItems: 'center',
    marginLeft: 0,
    marginRight: 0,
    marginTop: 0,
    gap: 4,
    ...(previewLayout === 'fluid' && {
      flex: '1 1 0',
      minWidth: 0,
      '& .MuiFormControlLabel-label': {
        width: '100%',
        margin: 0,
      },
    }),
    '&:has(.Mui-focusVisible)': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
      borderRadius: Number(theme.shape.borderRadius) * 2,
    },
  },
  hiddenRadio: {
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    width: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    whiteSpace: 'nowrap',
  },
  optionLabel: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
    cursor: 'pointer',
    width: previewLayout === 'fluid' ? '100%' : 'auto',
  },
  optionPreview: {
    width: previewLayout === 'fluid' ? '100%' : 'auto',
    alignSelf: previewLayout === 'fluid' ? 'stretch' : 'center',
  },
  optionLabelText: {
    textAlign: 'center',
    width: '100%',
  },
  fontBold: {
    fontWeight: theme.typography.fontWeightMedium,
  },
}));
